import React, { FC, useContext, useMemo } from 'react';
import { ScreenClassContext } from 'react-grid-system';

import { ResponsiveProps } from 'ia-react-core/~/interfaces/ResponsiveProps';
import COLOR_SCHEMES from '../../../constants/COLOR_SCHEMES';
import BREAKPOINT_LIST from '../../../constants/BREAKPOINT_LIST';
import ColorSchemeContext from '../../colorScheme/ColorSchemeContext';

import { StyledDivider } from './Divider.styles';

type DividerResponsiveProps = {
  /** Make the divider vertical. */
  vertical?: boolean;
  /** Make the divider horizontal. */
  horizontal?: boolean;
  /** Thickness of the divider in pixels. */
  size?: number;
};

export interface DividerProps extends DividerResponsiveProps, ResponsiveProps<DividerResponsiveProps> {
  /** Style the Divider in white. */
  white?: boolean;
  /** Style the Divider in black. */
  black?: boolean;
  /** Make the divider vertical at a specific breakpoint. Make the divider horizontal at a specific breakpoint. Make the divider dotted. */
  dotted?: boolean;
}

const DividerDefaultProps: Partial<DividerProps> = {
  size: 2,
};

const Divider: FC<DividerProps> = ({ vertical, ...props }) => {
  const { scheme } = useContext(ColorSchemeContext);
  const upperBreakpoint = useContext(ScreenClassContext);
  const indexOfBreakpoint = BREAKPOINT_LIST.indexOf(upperBreakpoint);
  const activeBreakpointList = BREAKPOINT_LIST.slice(0, indexOfBreakpoint + 1).reverse();

  const resolvedVertical = useMemo(() => {
    const activeVerticalBreakpoint = activeBreakpointList.find((breakpoint) => (
      typeof props[`vertical-${breakpoint}`] !== 'undefined'
    ));

    const activeHorizontalBreakpoint = activeBreakpointList.find((breakpoint) => (
      typeof props[`horizontal-${breakpoint}`] !== 'undefined'
    ));

    if (activeVerticalBreakpoint && activeHorizontalBreakpoint) {
      const currentBreakpoint = activeBreakpointList[0];
      if (currentBreakpoint === activeVerticalBreakpoint) return true;
      if (currentBreakpoint === activeHorizontalBreakpoint) return false;

      if (BREAKPOINT_LIST.indexOf(activeVerticalBreakpoint) > BREAKPOINT_LIST.indexOf(activeHorizontalBreakpoint)) {
        return true;
      }
      return false;
    }

    if (activeHorizontalBreakpoint) return false;
    if (activeVerticalBreakpoint) return true;

    return vertical;
  }, [activeBreakpointList, props, vertical]);

  return (
    <StyledDivider
      role="presentation"
      vertical={resolvedVertical}
      shaded={scheme === COLOR_SCHEMES.SHADE}
      darkened={scheme === COLOR_SCHEMES.DARK}
      {...props}
    >
    </StyledDivider>
  );
};

Divider.defaultProps = DividerDefaultProps;

export default Divider;
